/**
 * SmithChartWindow.js
 *
 * Draggable, resizable floating window displaying Smith chart for S-parameter data
 * Uses Plotly scattersmith trace type for native Smith chart support
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import Plot from './Plots/PlotlyChart';
import THEME from '../styles/theme';
import { sParamToSmithData, S_PARAM_COLORS } from '../utils/smithChartUtils';

const API_BASE_URL = '';

const SmithChartWindow = ({
  isOpen,
  onClose,
  componentId,
  componentName,
  initialPosition = { x: 100, y: 100 }
}) => {
  // Window state
  const [position, setPosition] = useState(initialPosition);
  const [size, setSize] = useState({ width: 500, height: 550 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // Data state
  const [s2pData, setS2pData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Trace visibility
  const [visibleTraces, setVisibleTraces] = useState({
    S11: true,
    S12: false,
    S21: false,
    S22: false
  });

  const windowRef = useRef(null);

  const debugLog = useCallback((message, data) => {
    console.debug('[SmithChart]', message, data);
  }, []);

  const fetchS2PData = useCallback(async () => {
    const token = localStorage.getItem('gchain_token');
    debugLog('fetchS2PData called', { componentId, hasToken: !!token });

    if (!token) {
      setError('Authentication required');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/components/${componentId}/s2p?full=true`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      debugLog('fetch response', { status: response.status, ok: response.ok });

      if (!response.ok) {
        throw new Error('Failed to fetch S2P data');
      }
      const data = await response.json();
      debugLog('fetched data keys', {
        dataKeys: Object.keys(data || {}),
        innerDataKeys: Object.keys(data?.data || {}),
        hasS11Mag: !!data?.data?.s11_mag_db,
        hasS11Phase: !!data?.data?.s11_phase_deg,
        freqCount: data?.data?.frequencies?.length
      });
      setS2pData(data);
    } catch (err) {
      debugLog('fetch error', { error: err.message });
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [componentId, debugLog]);

  // Fetch full S2P data when component opens
  useEffect(() => {
    debugLog('useEffect triggered', { isOpen, componentId, willFetch: !!(isOpen && componentId) });
    if (isOpen && componentId) {
      fetchS2PData();
    }
  }, [isOpen, componentId, fetchS2PData, debugLog]);

  // Dragging handlers
  const handleMouseDown = useCallback((e) => {
    if (e.target.closest('.resize-handle') || e.target.closest('.no-drag')) return;
    setIsDragging(true);
    setDragOffset({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  }, [position]);

  const handleMouseMove = useCallback((e) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - dragOffset.x,
        y: e.clientY - dragOffset.y
      });
    }
    if (isResizing) {
      const newWidth = Math.max(400, e.clientX - position.x);
      const newHeight = Math.max(400, e.clientY - position.y);
      setSize({ width: newWidth, height: newHeight });
    }
  }, [isDragging, isResizing, dragOffset, position]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setIsResizing(false);
  }, []);

  // Resize handler
  const handleResizeMouseDown = useCallback((e) => {
    e.stopPropagation();
    setIsResizing(true);
  }, []);

  // Global mouse events for drag/resize
  useEffect(() => {
    if (isDragging || isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, isResizing, handleMouseMove, handleMouseUp]);

  // Toggle trace visibility
  const toggleTrace = (param) => {
    setVisibleTraces(prev => ({
      ...prev,
      [param]: !prev[param]
    }));
  };

  // Build Plotly traces from S2P data
  const buildTraces = () => {
    if (!s2pData?.data) return [];

    const traces = [];
    const { data } = s2pData;

    // S11 trace
    if (visibleTraces.S11 && data.s11_mag_db && data.s11_phase_deg) {
      const smithData = sParamToSmithData(data.s11_mag_db, data.s11_phase_deg);
      traces.push({
        type: 'scattersmith',
        real: smithData.real,
        imag: smithData.imag,
        name: 'S11',
        mode: 'lines',
        line: { color: S_PARAM_COLORS.S11, width: 2 },
        text: data.frequencies.map(f => `${f.toFixed(1)} MHz`),
        hovertemplate: 'S11<br>%{text}<br>Z: %{real:.2f} + j%{imag:.2f}<extra></extra>'
      });
    }

    // S12 trace
    if (visibleTraces.S12 && data.s12_mag_db && data.s12_phase_deg) {
      const smithData = sParamToSmithData(data.s12_mag_db, data.s12_phase_deg);
      traces.push({
        type: 'scattersmith',
        real: smithData.real,
        imag: smithData.imag,
        name: 'S12',
        mode: 'lines',
        line: { color: S_PARAM_COLORS.S12, width: 2 },
        text: data.frequencies.map(f => `${f.toFixed(1)} MHz`),
        hovertemplate: 'S12<br>%{text}<br>Z: %{real:.2f} + j%{imag:.2f}<extra></extra>'
      });
    }

    // S21 trace
    if (visibleTraces.S21 && data.s21_mag_db && data.s21_phase_deg) {
      const smithData = sParamToSmithData(data.s21_mag_db, data.s21_phase_deg);
      traces.push({
        type: 'scattersmith',
        real: smithData.real,
        imag: smithData.imag,
        name: 'S21',
        mode: 'lines',
        line: { color: S_PARAM_COLORS.S21, width: 2 },
        text: data.frequencies.map(f => `${f.toFixed(1)} MHz`),
        hovertemplate: 'S21<br>%{text}<br>Z: %{real:.2f} + j%{imag:.2f}<extra></extra>'
      });
    }

    // S22 trace
    if (visibleTraces.S22 && data.s22_mag_db && data.s22_phase_deg) {
      const smithData = sParamToSmithData(data.s22_mag_db, data.s22_phase_deg);
      traces.push({
        type: 'scattersmith',
        real: smithData.real,
        imag: smithData.imag,
        name: 'S22',
        mode: 'lines',
        line: { color: S_PARAM_COLORS.S22, width: 2 },
        text: data.frequencies.map(f => `${f.toFixed(1)} MHz`),
        hovertemplate: 'S22<br>%{text}<br>Z: %{real:.2f} + j%{imag:.2f}<extra></extra>'
      });
    }

    return traces;
  };

  const plotLayout = {
    autosize: true,
    margin: { l: 40, r: 40, t: 20, b: 40 },
    paper_bgcolor: THEME.cardBackground,
    font: {
      color: THEME.textPrimary,
      family: 'Inter, system-ui, sans-serif',
      size: 11
    },
    smith: {
      bgcolor: THEME.cardBackground,
      imaginaryaxis: {
        gridcolor: '#404040',
        linecolor: '#606060'
      },
      realaxis: {
        gridcolor: '#404040',
        linecolor: '#606060'
      }
    },
    showlegend: true,
    legend: {
      x: 0.5,
      y: -0.15,
      xanchor: 'center',
      orientation: 'h',
      font: { size: 11 }
    }
  };

  const plotConfig = {
    displayModeBar: true,
    displaylogo: false,
    modeBarButtonsToRemove: ['select2d', 'lasso2d', 'autoScale2d'],
    toImageButtonOptions: {
      format: 'png',
      filename: `smith_chart_${componentName}`,
      height: 800,
      width: 800,
      scale: 2
    }
  };

  if (!isOpen) return null;

  const freqRange = s2pData?.data?.frequencies
    ? `${s2pData.data.frequencies[0]?.toFixed(0)} - ${s2pData.data.frequencies[s2pData.data.frequencies.length - 1]?.toFixed(0)} MHz`
    : '';

  return (
    <WindowContainer
      ref={windowRef}
      style={{
        left: position.x,
        top: position.y,
        width: size.width,
        height: size.height
      }}
    >
      <TitleBar onMouseDown={handleMouseDown}>
        <TitleText>
          Smith Chart - {componentName}
          {freqRange && <FreqRange>{freqRange}</FreqRange>}
        </TitleText>
        <CloseButton className="no-drag" onClick={onClose}>&times;</CloseButton>
      </TitleBar>

      <ControlBar className="no-drag">
        <TraceToggle
          $active={visibleTraces.S11}
          $color={S_PARAM_COLORS.S11}
          onClick={() => toggleTrace('S11')}
        >
          S11
        </TraceToggle>
        <TraceToggle
          $active={visibleTraces.S12}
          $color={S_PARAM_COLORS.S12}
          onClick={() => toggleTrace('S12')}
        >
          S12
        </TraceToggle>
        <TraceToggle
          $active={visibleTraces.S21}
          $color={S_PARAM_COLORS.S21}
          onClick={() => toggleTrace('S21')}
        >
          S21
        </TraceToggle>
        <TraceToggle
          $active={visibleTraces.S22}
          $color={S_PARAM_COLORS.S22}
          onClick={() => toggleTrace('S22')}
        >
          S22
        </TraceToggle>
        <ImpedanceLabel>Z₀ = 50Ω</ImpedanceLabel>
      </ControlBar>

      <ChartContainer className="no-drag">
        {loading && (
          <LoadingOverlay>
            <LoadingText>Loading S2P data...</LoadingText>
          </LoadingOverlay>
        )}
        {error && (
          <ErrorOverlay>
            <ErrorText>Error: {error}</ErrorText>
          </ErrorOverlay>
        )}
        <Plot
          data={s2pData?.data ? buildTraces() : [{
            type: 'scattersmith',
            real: [],
            imag: [],
            mode: 'lines',
            name: 'No Data'
          }]}
          layout={plotLayout}
          config={plotConfig}
          style={{ width: '100%', height: '100%' }}
          useResizeHandler={true}
        />
      </ChartContainer>

      <ResizeHandle
        className="resize-handle"
        onMouseDown={handleResizeMouseDown}
      />
    </WindowContainer>
  );
};

// Styled Components
const WindowContainer = styled.div`
  position: fixed;
  background: ${THEME.panelBackground};
  border: 1px solid ${THEME.cardBorder};
  border-radius: 8px;
  box-shadow: ${THEME.shadowLarge};
  z-index: 9999;
  display: flex;
  flex-direction: column;
  min-width: 400px;
  min-height: 400px;
`;

const TitleBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: ${THEME.cardBackground};
  border-bottom: 1px solid ${THEME.cardBorder};
  border-radius: 8px 8px 0 0;
  cursor: move;
  user-select: none;
`;

const TitleText = styled.div`
  color: ${THEME.textPrimary};
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const FreqRange = styled.span`
  color: ${THEME.textSecondary};
  font-size: 12px;
  font-weight: 400;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${THEME.textSecondary};
  font-size: 20px;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all ${THEME.transitionFast};

  &:hover {
    background: ${THEME.accentPrimary};
    color: white;
  }
`;

const ControlBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-bottom: 1px solid ${THEME.cardBorder};
  background: ${THEME.cardBackground};
`;

const TraceToggle = styled.button`
  padding: 4px 10px;
  border: 2px solid ${props => props.$color};
  border-radius: 4px;
  background: ${props => props.$active ? props.$color : 'transparent'};
  color: ${props => props.$active ? '#000' : props.$color};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all ${THEME.transitionFast};

  &:hover {
    opacity: 0.8;
  }
`;

const ImpedanceLabel = styled.span`
  margin-left: auto;
  color: ${THEME.textSecondary};
  font-size: 12px;
`;

const ChartContainer = styled.div`
  flex: 1;
  position: relative;
  overflow: hidden;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${THEME.cardBackground};
`;

const LoadingText = styled.div`
  color: ${THEME.textSecondary};
  font-size: 14px;
`;

const ErrorOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${THEME.cardBackground};
`;

const ErrorText = styled.div`
  color: ${THEME.error};
  font-size: 14px;
`;

const ResizeHandle = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  width: 16px;
  height: 16px;
  cursor: nwse-resize;

  &::after {
    content: '';
    position: absolute;
    bottom: 4px;
    right: 4px;
    width: 8px;
    height: 8px;
    border-right: 2px solid ${THEME.textMuted};
    border-bottom: 2px solid ${THEME.textMuted};
  }
`;

export default SmithChartWindow;
